import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { CheckCircle2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ code: "", postcode: "", address: "", email: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    setForm(f => ({ ...f, code: params.get("code") || "" }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() && !form.postcode.trim() && !form.address.trim() && !form.email.trim()) {
      toast.error("Please provide at least one identifier (reference code, postcode, address or email).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/unsubscribe`, form);
      setDone(true);
    } catch { toast.error("Something went wrong. Please email us directly."); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="unsub-success" className="bg-[#0B0F0E] min-h-[80vh]">
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <CheckCircle2 className="text-[#C9A24B] mx-auto" size={56} />
          <h1 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-7 leading-[0.95]">You have been removed.</h1>
          <p className="text-[#9BA39B] text-lg mt-6 max-w-xl mx-auto leading-relaxed">
            Your details have been added to our permanent suppression list. You will not receive further mailings from Upsurge Strategic Land. Allow up to five working days for any mail already in transit.
          </p>
          <Link to="/" data-testid="unsub-home" className="usl-btn-outline mt-10 inline-flex">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="unsubscribe-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12">
        <div className="overline"><span className="h-line" />Mailing Preferences</div>
        <h1 className="font-display text-[#F4EFE6] text-5xl md:text-6xl mt-7 leading-[0.95]">
          Remove me from <span className="italic-gold">future mailings.</span>
        </h1>
        <p className="text-[#9BA39B] text-lg mt-8 leading-relaxed">
          Use the form below to be permanently suppressed from our postal mailings. The easiest identifier is the reference code printed on your letter — but any of the fields will work.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-28">
        <form data-testid="unsub-form" onSubmit={submit} className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12 space-y-7">
          <div>
            <label className="overline block mb-2">Letter reference code (preferred)</label>
            <input data-testid="unsub-code" className="underline-input uppercase tracking-[0.18em]" value={form.code} onChange={onChange("code")} placeholder="USL-XXXXX" />
          </div>
          <div className="grid md:grid-cols-2 gap-7 border-t border-[#F4EFE6]/10 pt-7">
            <div>
              <label className="overline block mb-2">Postcode</label>
              <input data-testid="unsub-postcode" className="underline-input uppercase" value={form.postcode} onChange={onChange("postcode")} />
            </div>
            <div>
              <label className="overline block mb-2">Email (if known)</label>
              <input data-testid="unsub-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Address</label>
              <input data-testid="unsub-address" className="underline-input" value={form.address} onChange={onChange("address")} placeholder="House name / number, street, town" />
            </div>
            <div className="md:col-span-2">
              <label className="overline block mb-2">Reason (optional)</label>
              <textarea data-testid="unsub-reason" rows={3} className="underline-input resize-none" value={form.reason} onChange={onChange("reason")} placeholder="Helpful for us — but not required" />
            </div>
          </div>
          <button data-testid="unsub-submit" type="submit" disabled={loading} className="usl-btn">
            {loading ? "Processing..." : "Remove me permanently"} <ArrowUpRight size={16} />
          </button>
          <p className="text-xs text-[#9BA39B] max-w-xl">
            We retain the minimum information needed to ensure you are not re-contacted — required under UK GDPR Recital 47.
          </p>
        </form>
      </section>
    </div>
  );
}
